import { isFuture, isValid } from 'date-fns';
import { useFormikContext } from 'formik';
import trim from 'lodash/trim';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';

import { isEmpty } from 'app/utils';

import { EmployeeFileUploadContent } from './EmployeeFileUploadContent';
import { employeesTemplateHeaders, validCivilStatuses } from './headers';
import type { EmployeeXLSX, EmployerConnection } from '../../../../types';
import { employeeService } from '../../Services/EmployeeService';

const collectDuplicatePersonnelNumberErrors = (employees: EmployeeXLSX[], rowOffset: number): any[] => {
  const counts = new Map<string, number[]>();
  employees.forEach((employee, index) => {
    if (employee.personnelNumber) {
      const num = String(employee.personnelNumber).trim();
      const existing = counts.get(num) ?? [];
      counts.set(num, [...existing, index]);
    }
  });

  const errors: any[] = [];
  Array.from(counts.values()).forEach(indices => {
    if (indices.length > 1) {
      indices.forEach(index => {
        errors.push({
          message: 'employees.error.personalInformation.personnelNumberDuplicate',
          values: index + rowOffset + 1,
        });
      });
    }
  });
  return errors;
};

type EmployeeFileUploadProps = {
  employerId: string;
  employerConnections: EmployerConnection[] | null;
  progressMsg: string | null;
  setProgressMsg: React.Dispatch<React.SetStateAction<string | null>>;
};

const EmployeeFileUpload = ({
  employerId,
  employerConnections,
  progressMsg,
  setProgressMsg,
}: EmployeeFileUploadProps) => {
  const employeesTemplate = new URL('./employees-template.xlsx', import.meta.url).href;
  const [fileErrors, setFileErrors] = useState<any[]>([]);
  const { isSubmitting, setFieldValue, setFieldError, setFieldTouched, values } = useFormikContext<any>();
  const [validating, setValidating] = useState(false);
  const rowOffset = 19;

  const getSelectedConnections = useCallback(() => {
    if (!employerConnections) return [];
    const selected = values.connectionId;
    if (!selected || (Array.isArray(selected) && selected.length === 0)) return [];
    const selectedIds = Array.isArray(selected) ? selected : [selected];
    return employerConnections.filter(c => selectedIds.includes(c.connectionId));
  }, [employerConnections, values.connectionId]);

  const childIsValid = useCallback(child => {
    let personNameValid = true;
    let dateOfBirthValid = true;
    let genderValid = true;

    if (!child.personName.lastName) {
      personNameValid = false;
    }

    if (!child.dateOfBirth) {
      dateOfBirthValid = false;
    }

    if (!child.gender) {
      genderValid = false;
    }
    return personNameValid && dateOfBirthValid && genderValid;
  }, []);

  const childIsEmpty = useCallback(child => {
    return (
      isEmpty(child.personName.firstName) &&
      isEmpty(child.personName.lastName) &&
      isEmpty(child.gender) &&
      isEmpty(child.dateOfBirth)
    );
  }, []);

  const partnerIsValid = useCallback(partner => {
    return !isEmpty(partner.personName.lastName) && !isEmpty(partner.gender) && !isEmpty(partner.dateOfBirth);
  }, []);

  const employeePersonalInformationIsValid = useCallback(employee => {
    return (
      !isEmpty(employee.personName.lastName) &&
      !isEmpty(employee.gender) &&
      !isEmpty(employee.dateOfBirth) &&
      !isEmpty(employee.civilStatus) &&
      !isEmpty(employee.gender) &&
      !isEmpty(employee.dateOfBirth) &&
      !isEmpty(employee.socialSecurityNumber)
    );
  }, []);

  const personnelNumberIsValid = useCallback(async (employerId, selectedConnections, employee) => {
    for (const connection of selectedConnections) {
      try {
        await employeeService.validateField(
          employerId,
          'personnelNumber',
          employee.personnelNumber,
          connection.connectionId,
        );
      } catch (error: any) {
        if (error.response?.status === 422) {
          return false;
        }
        throw error;
      }
    }

    return true;
  }, []);

  const employeeContactInformationIsValid = useCallback(employee => {
    return (
      !isEmpty(employee.address.street) &&
      !isEmpty(employee.address.houseNumber) &&
      !isEmpty(employee.address.zipCode) &&
      !isEmpty(employee.address.city) &&
      !isEmpty(employee.address.country)
    );
  }, []);

  const employmentYearsFromInformationIsValid = useCallback(employment => {
    if (isEmpty(employment.yearsFrom)) {
      return true;
    }

    // Default format date
    let yearsFrom = new Date(employment.yearsFrom);

    if (isValid(yearsFrom)) {
      return !isFuture(yearsFrom);
    }

    // dd-mm-YYYY format date
    const yearsFromDateArray = employment.yearsFrom.split('-');
    if (yearsFromDateArray.length === 3 && yearsFromDateArray[2].length === 4) {
      const yearsFromDateArrayParsedInt = yearsFromDateArray.map((el: any) => parseInt(el));

      yearsFrom = new Date(
        yearsFromDateArrayParsedInt[2],
        yearsFromDateArrayParsedInt[1] - 1,
        yearsFromDateArrayParsedInt[0],
      );
    }

    if (isValid(yearsFrom)) {
      return !isFuture(yearsFrom);
    }

    return false;
  }, []);

  const validateSingleEmployee = useCallback(
    async (employee: EmployeeXLSX, index: number, selectedConnections: EmployerConnection[]): Promise<any[]> => {
      const errors: any[] = [];

      for (const childrenKey in employee.children) {
        if (!childIsValid(employee.children[childrenKey])) {
          errors.push({ message: 'employees.error.children', values: index + rowOffset + 1 });
        }
      }

      if (validCivilStatuses.includes(employee.civilStatus)) {
        if (!partnerIsValid(employee.partners)) {
          employee.partners = null;
        }
      } else {
        employee.partners = null;
      }

      if (!employeePersonalInformationIsValid(employee)) {
        errors.push({ message: 'employees.error.personalInformation', values: index + rowOffset + 1 });
      }

      if (
        selectedConnections.length > 0 &&
        !(await personnelNumberIsValid(employerId, selectedConnections, employee))
      ) {
        errors.push({
          message: 'employees.error.personalInformation.personnelNumberExists',
          values: index + rowOffset + 1,
        });
      }

      if (!employeeContactInformationIsValid(employee)) {
        errors.push({ message: 'employees.error.contactInformation', values: index + rowOffset + 1 });
      }

      if (!employmentYearsFromInformationIsValid(employee.employment)) {
        errors.push({ message: 'employees.error.employment.yearsFrom', values: index + rowOffset + 1 });
      }

      return errors;
    },
    [
      childIsValid,
      employeeContactInformationIsValid,
      employeePersonalInformationIsValid,
      employmentYearsFromInformationIsValid,
      partnerIsValid,
      employerId,
      personnelNumberIsValid,
    ],
  );

  const validateEmployees = useCallback(
    async (employees: EmployeeXLSX[], selectedConnections: EmployerConnection[]) => {
      try {
        const duplicateErrors = collectDuplicatePersonnelNumberErrors(employees, rowOffset);
        const employeeErrors: any[] = [...duplicateErrors];

        for (let index = 0; index < employees.length; index++) {
          const singleErrors = await validateSingleEmployee(employees[index], index, selectedConnections);
          employeeErrors.push(...singleErrors);
        }

        setFileErrors(employeeErrors);
        setFieldValue('hasErrors', employeeErrors.length > 0);

        return employeeErrors;
      } finally {
        setProgressMsg('employees.add.validateFileFinish');
        setValidating(false);
      }
    },
    [validateSingleEmployee, setFieldValue, setProgressMsg],
  );

  const parseExcelFile = useCallback(
    file => {
      return new Promise((resolve, reject) => {
        setFileErrors([]);
        const reader = new FileReader();
        reader.onload = event => {
          try {
            const payload = event.target?.result;
            const workbook = XLSX.read(payload, { type: 'binary', dateNF: 'dd/MM/yyyy' });
            const sheet = workbook.Sheets[Object.keys(workbook.Sheets)[0]];
            const range = XLSX.utils.decode_range(sheet['!ref']!);

            range.s.r = rowOffset;

            const data: any[] = XLSX.utils.sheet_to_json(sheet, {
              header: employeesTemplateHeaders,
              blankrows: false,
              skipHidden: true,
              rawNumbers: true,
              raw: false,
              range: range,
            });

            const employees: EmployeeXLSX[] = [];

            if (data.length > 1000) {
              setFileErrors([...fileErrors, { message: 'employees.error.fileTooBig' }]);
            }

            if (!data.length) {
              setFileErrors([...fileErrors, { message: 'employees.error.fileEmpty' }]);
            }

            data.forEach(row => {
              if (Object.values(row).every(value => value === ' ')) {
                return;
              }
              const wage = parseFloat(row['employment.wage.grossWage']);
              const partTimePercentage = parseFloat(row['employment.partTimePercentage']);

              const partner = {
                socialSecurityNumber: row['partner.socialSecurityNumber'],
                personName: {
                  initials: row['partner.personName.initials'],
                  firstName: row['partner.personName.firstName'],
                  lastNamePrefix: row['partner.personName.lastNamePrefix'],
                  lastName: row['partner.personName.lastName'],
                },
                gender: row['partner.gender'],
                dateOfBirth: row['partner.dateOfBirth'],
                dateOfDeath: row['partner.dateOfDeath'],
                startOfRelationship: row['partner.startOfRelationship'],
                endOfRelationship: row['partner.endOfRelationship'],
              };

              const children = [];
              for (let i = 0; i <= 2; i++) {
                const child = {
                  socialSecurityNumber: row[`children[${i}].socialSecurityNumber`],
                  personName: {
                    initials: row[`children[${i}].personName.initials`],
                    firstName: row[`children[${i}].personName.firstName`],
                    lastNamePrefix: row[`children[${i}].personName.lastNamePrefix`],
                    lastName: row[`children[${i}].personName.lastName`],
                  },
                  gender: row[`children[${i}].gender`],
                  dateOfBirth: row[`children[${i}].dateOfBirth`],
                };
                if (!childIsEmpty(child)) {
                  children.push(child);
                }
              }

              const employee: EmployeeXLSX = {
                personnelNumber: row.personnelNumber ?? null,
                socialSecurityNumber: row.socialSecurityNumber ?? null,
                personName: {
                  initials: row['personName.initials'] ?? null,
                  firstName: row['personName.firstName'],
                  lastNamePrefix: row['personName.lastNamePrefix'] ?? null,
                  lastName: row['personName.lastName'] ?? null,
                },
                civilStatus: row.civilStatus?.toLowerCase() ?? null,
                gender: row.gender ?? null,
                dateOfBirth: row.dateOfBirth ?? null,
                dateOfDeath: row.dateOfDeath ?? null,
                emailAddress: row.emailAddress ?? null,
                costCenter: row['costCenter'] ?? null,
                profession: row['profession'] ?? null,
                address: {
                  street: row['address.street'] ?? null,
                  houseNumber: row['address.houseNumber'] ?? null,
                  houseNumberSuffix: row['address.houseNumberSuffix'] ?? null,
                  zipCode: row['address.zipCode'] !== undefined ? trim(row['address.zipCode']) : null,
                  city: row['address.city'] ?? null,
                  country: row['address.country'] ?? null,
                },
                nationality: row['nationality'] ?? null,
                employment: {
                  partTimePercentage: partTimePercentage ?? null,
                  startDate: row['employment.startDate'] ?? null,
                  yearsFrom: row['employment.yearsFrom'] ?? null,
                  endDate: row['employment.endDate'] ?? null,
                  emoluments: row['employment.emoluments'] ?? null,
                  wage: {
                    grossWage: wage ?? null,
                    fullTime: partTimePercentage === 100.0,
                  },
                },
                partners: partner ?? null,
                children: children ?? null,
              };

              employees.push(employee);
            });

            resolve(employees);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => {
          reject(new Error(`Error occurred reading file: ${file.name}`));
        };
        reader.readAsBinaryString(file);
      });
    },
    [childIsEmpty, fileErrors],
  );

  // Re-validation logic when connection changes
  const revalidateRef = useRef<() => void>(() => undefined);
  revalidateRef.current = () => {
    const employees: EmployeeXLSX[] = values.employees;
    if (!employees || employees.length === 0) return;
    const selectedConnections = getSelectedConnections();
    if (selectedConnections.length === 0) return;
    setFileErrors([]);
    setValidating(true);
    setProgressMsg('employees.add.validateFile');
    validateEmployees(employees, selectedConnections);
  };

  const prevConnectionIdRef = useRef(values.connectionId);

  useEffect(() => {
    const prev = prevConnectionIdRef.current;
    prevConnectionIdRef.current = values.connectionId;

    const toKey = (val: unknown) =>
      Array.isArray(val)
        ? [...val].sort((a: string, b: string) => a.localeCompare(b)).join(',')
        : (val as string) ?? '';

    if (toKey(prev) === toKey(values.connectionId)) return;

    revalidateRef.current();
  }, [values.connectionId]);

  const changeList = useCallback(
    async files => {
      const file = files.slice(0, 1)[0] ?? undefined;

      if (file) {
        setProgressMsg('employees.add.readFile');
        setValidating(true);
        await parseExcelFile(file)
          .then(async employees => {
            setProgressMsg('employees.add.validateFile');
            const selectedConnections = getSelectedConnections();
            if (selectedConnections.length === 0) {
              setFieldTouched('connectionId', true);
              setProgressMsg(null);
              setValidating(false);
              setFieldValue('hasErrors', false);
            } else {
              await validateEmployees(employees as EmployeeXLSX[], selectedConnections);
            }
            setFieldValue('employees', employees);
          })
          .catch(error => {
            setFileErrors([...fileErrors, error]);
            setFieldError('employees', error);
          });
      } else {
        setProgressMsg('employees.add.fileClear');
        setFileErrors([]);
        setFieldValue('employees', []);
      }
    },
    [
      fileErrors,
      setFieldTouched,
      getSelectedConnections,
      parseExcelFile,
      setFieldError,
      setFieldValue,
      setProgressMsg,
      validateEmployees,
    ],
  );

  return (
    <EmployeeFileUploadContent
      employerConnections={employerConnections}
      employeesTemplate={employeesTemplate}
      changeList={changeList}
      isSubmitting={isSubmitting}
      fileErrors={fileErrors}
      validating={validating}
      progressMsg={progressMsg}
    />
  );
};

export default EmployeeFileUpload;
